import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { I18nService } from '../../../@core/services/i18n/i18n.service';
import { ModalService } from '../../../@core/services/modal/modal.service';
import { Dialog } from '../../../@core/services/modal/modal.interfaces';
import { LocaleDatePipe } from '../../../shared/pipes/locale-date.pipe';
import { DialogButtonsDirective } from '../../../shared/components/modal-dialog/dialog-buttons.directive';
import { VatIdCheckResult, VatIdCheckType } from '../../../generated/generated-types';
import { VatIdVerificationService } from '../../crm/services/vat-id-verification.service';
import { VatIdReportDialogComponent, VatVerificationRow } from './vat-id-report-dialog.component';
import type { VatCheckTarget } from './formly-vat-id-input.types';

type TagSeverity = 'success' | 'danger' | 'warn';

@Component({
  selector: 'app-vat-id-history-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './vat-id-history-dialog.component.html',
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    TableModule,
    TagModule,
    LocaleDatePipe,
    DialogButtonsDirective,
  ],
})
export class VatIdHistoryDialogComponent implements Dialog<void>, OnInit {
  resolveWith!: (result?: void) => void;
  anchorId!: string;
  target: VatCheckTarget = 'address';

  private readonly vatIdVerificationService = inject(VatIdVerificationService);
  private readonly modalService = inject(ModalService);
  private readonly i18nService = inject(I18nService);

  readonly rows = signal<VatVerificationRow[]>([]);
  readonly loading = signal(true);

  ngOnInit(): void {
    this.historyRows().subscribe((rows) => {
      this.rows.set(rows ?? []);
      this.loading.set(false);
    });
  }

  private historyRows() {
    if (this.target === 'postalAddress') {
      return this.vatIdVerificationService.getHistory(this.anchorId).mapSingle((data) => data.getVatIdHistory);
    }
    return this.vatIdVerificationService.getHistoryForAddress(this.anchorId).mapSingle((data) => data.getVatIdHistoryForAddress);
  }

  resultSeverity(row: VatVerificationRow): TagSeverity {
    switch (row.result) {
      case VatIdCheckResult.VALID:
        return 'success';
      case VatIdCheckResult.INVALID:
        return 'danger';
      default:
        return 'warn';
    }
  }

  resultLabel(row: VatVerificationRow): string {
    switch (row.result) {
      case VatIdCheckResult.VALID:
        return 'address.vat-result-tag-valid';
      case VatIdCheckResult.INVALID:
        return 'address.vat-result-tag-invalid';
      case VatIdCheckResult.ERROR:
        return _('address.vat-result-tag-error');
      default:
        return _('address.vat-result-tag-error');
    }
  }

  checkTypeLabel(row: VatVerificationRow): string {
    return row.checkType === VatIdCheckType.QUALIFIED
      ? 'address.vat-check-type-qualified'
      : 'address.vat-check-type-simple';
  }

  /** VIES timestamp when present; otherwise the row's createdAt so ERROR rows still show a date. */
  historyDate(row: VatVerificationRow): Date | string | null {
    return row.requestDate ?? row.createdAt ?? null;
  }

  openReport(row: VatVerificationRow): void {
    this.modalService
      .fromComponent(VatIdReportDialogComponent, {
        size: 'lg',
        closable: true,
        title: this.i18nService.translate(_('address.vat-report-title')),
        locals: { verification: row },
      })
      .subscribe();
  }

  close(): void {
    this.resolveWith();
  }
}
